import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  MapPin, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Upload, 
  Users, 
  Shield,
  HelpCircle,
  MousePointer,
  Keyboard,
  Eye,
  Filter,
  Map,
  ArrowLeft
} from "lucide-react";

export default function HelpPage() {
  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <Link to="/">
          <Button variant="outline" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Вернуться на главную
          </Button>
        </Link>
        <h1 className="text-3xl font-bold mb-2">Руководство пользователя</h1>
        <p className="text-muted-foreground">
          Полное руководство по работе с геоинформационной системой
        </p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Обзор</TabsTrigger>
          <TabsTrigger value="objects">Объекты</TabsTrigger>
          <TabsTrigger value="map">Карта</TabsTrigger>
          <TabsTrigger value="search">Поиск</TabsTrigger>
          <TabsTrigger value="admin">Администрирование</TabsTrigger>
          <TabsTrigger value="shortcuts">Горячие клавиши</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  Добро пожаловать в ГИС GeoPulseML
                </CardTitle>
                <CardDescription>
                  Геоинформационная система для управления пространственными данными
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>
                  ГИС GeoPulseML - это современная веб-платформа для работы с геопространственными данными,
                  которая позволяет создавать, редактировать и анализировать объекты на карте.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold">Основные возможности:</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        Создание и редактирование объектов на карте
                      </li>
                      <li className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-primary" />
                        Поиск объектов по различным критериям
                      </li>
                      <li className="flex items-center gap-2">
                        <Upload className="h-4 w-4 text-primary" />
                        Импорт и экспорт данных
                      </li>
                      <li className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        Управление пользователями и правами доступа
                      </li>
                    </ul>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold">Поддерживаемые форматы:</h4>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">GeoJSON</Badge>
                      <Badge variant="secondary">KML</Badge>
                      <Badge variant="secondary">Excel</Badge>
                      <Badge variant="secondary">CSV</Badge>
                      <Badge variant="secondary">Shapefile</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Начало работы</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-4 border rounded-lg">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-primary font-bold">1</span>
                    </div>
                    <h4 className="font-semibold mb-2">Авторизация</h4>
                    <p className="text-sm text-muted-foreground">
                      Войдите в систему или зарегистрируйтесь для доступа к функциям
                    </p>
                  </div>
                  
                  <div className="text-center p-4 border rounded-lg">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-primary font-bold">2</span>
                    </div>
                    <h4 className="font-semibold mb-2">Изучение интерфейса</h4>
                    <p className="text-sm text-muted-foreground">
                      Ознакомьтесь с основными элементами карты и панелями управления
                    </p>
                  </div>
                  
                  <div className="text-center p-4 border rounded-lg">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-primary font-bold">3</span>
                    </div>
                    <h4 className="font-semibold mb-2">Создание объектов</h4>
                    <p className="text-sm text-muted-foreground">
                      Начните создавать свои первые объекты на карте
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Objects Tab */}
        <TabsContent value="objects">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Работа с объектами
                </CardTitle>
                <CardDescription>
                  Создание, редактирование и управление объектами на карте
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-3">Создание нового объекта</h4>
                  <ol className="space-y-2 text-sm">
                    <li>1. Нажмите кнопку <Badge variant="outline">+ Создать объект</Badge> на левой панели</li>
                    <li>2. Заполните обязательные поля: название, тип объекта, орган управления</li>
                    <li>3. Добавьте описание и выберите статус объекта</li>
                    <li>4. Нажмите "Создать" для сохранения</li>
                  </ol>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold mb-3">Добавление геометрии</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    После создания объекта можно добавить его геометрию на карте:
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <MousePointer className="h-4 w-4" />
                      <strong>Точка:</strong> Кликните на карте в нужном месте
                    </li>
                    <li className="flex items-center gap-2">
                      <MousePointer className="h-4 w-4" />
                      <strong>Линия:</strong> Кликните для начала, затем добавляйте точки
                    </li>
                    <li className="flex items-center gap-2">
                      <MousePointer className="h-4 w-4" />
                      <strong>Полигон:</strong> Обведите область, замкнув линию
                    </li>
                  </ul>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold mb-3">Редактирование объектов</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Edit className="h-4 w-4 text-blue-500" />
                      Нажмите на объект в списке для редактирования свойств
                    </li>
                    <li className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-green-500" />
                      Используйте кнопку "Показать на карте" для центрирования
                    </li>
                    <li className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4 text-red-500" />
                      Удаление объекта требует подтверждения
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Типы объектов</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium mb-2">Основные типы:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Здания (жилые, коммерческие)</li>
                      <li>• Инфраструктура (дороги, коммуникации)</li>
                      <li>• Земельные участки</li>
                      <li>• Площадки и территории</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium mb-2">Статусы объектов:</h5>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800">Активный</Badge>
                        <span className="text-sm">Объект в эксплуатации</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-yellow-100 text-yellow-800">Планируемый</Badge>
                        <span className="text-sm">Объект в разработке</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-100 text-red-800">Неактивный</Badge>
                        <span className="text-sm">Объект выведен из эксплуатации</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Map Tab */}
        <TabsContent value="map">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Map className="h-5 w-5" />
                Работа с картой
              </CardTitle>
              <CardDescription>
                Навигация, инструменты рисования и слои карты
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3">Навигация по карте</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium mb-2">Управление мышью:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Перетаскивание - перемещение карты</li>
                      <li>• Колесо мыши - масштабирование</li>
                      <li>• Двойной клик - увеличение масштаба</li>
                      <li>• Правый клик - контекстное меню</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium mb-2">Кнопки управления:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• + / - - масштабирование</li>
                      <li>• 🏠 - возврат к начальному виду</li>
                      <li>• 📍 - определение местоположения</li>
                      <li>• 📐 - измерение расстояний</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold mb-3">Инструменты рисования</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-3 border rounded">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                    <h5 className="font-medium">Точка</h5>
                    <p className="text-xs text-muted-foreground">Клик для размещения</p>
                  </div>
                  <div className="text-center p-3 border rounded">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <div className="w-4 h-px bg-green-600"></div>
                    </div>
                    <h5 className="font-medium">Линия</h5>
                    <p className="text-xs text-muted-foreground">Клики для создания</p>
                  </div>
                  <div className="text-center p-3 border rounded">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <div className="w-3 h-3 border border-purple-600"></div>
                    </div>
                    <h5 className="font-medium">Полигон</h5>
                    <p className="text-xs text-muted-foreground">Замкнутая область</p>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold mb-3">Слои карты</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Управляйте видимостью различных слоев через панель слоев:
                </p>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Базовая карта:</strong> OpenStreetMap, спутниковые снимки</li>
                  <li>• <strong>Объекты:</strong> созданные пользователями объекты</li>
                  <li>• <strong>Кластеры:</strong> группировка близко расположенных объектов</li>
                  <li>• <strong>Подложки:</strong> дополнительные слои данных</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Search Tab */}
        <TabsContent value="search">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Поиск и фильтрация
              </CardTitle>
              <CardDescription>
                Быстрый поиск объектов и расширенная фильтрация
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3">Быстрый поиск</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Используйте строку поиска в заголовке для быстрого поиска:
                </p>
                <ul className="space-y-2 text-sm">
                  <li>• По названию объекта</li>
                  <li>• По адресу или местоположению</li>
                  <li>• По описанию объекта</li>
                  <li>• По типу объекта</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold mb-3">Расширенные фильтры</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium mb-2">Фильтры объектов:</h5>
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        По типу объекта
                      </li>
                      <li className="flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        По органу управления
                      </li>
                      <li className="flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        По статусу
                      </li>
                      <li className="flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        По дате создания
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium mb-2">Пространственный поиск:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Поиск в видимой области</li>
                      <li>• Поиск в радиусе от точки</li>
                      <li>• Поиск внутри полигона</li>
                      <li>• Поиск по координатам</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold mb-3">Результаты поиска</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Результаты отображаются в виде:
                </p>
                <ul className="space-y-2 text-sm">
                  <li>• Список объектов на левой панели</li>
                  <li>• Выделенные объекты на карте</li>
                  <li>• Счетчик найденных объектов</li>
                  <li>• Возможность экспорта результатов</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Tab */}
        <TabsContent value="admin">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Администрирование
              </CardTitle>
              <CardDescription>
                Управление пользователями, правами доступа и системными настройками
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3">Доступ к админ-панели</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Для доступа к административным функциям необходимы права администратора.
                  Панель доступна через меню профиля → "Админ-панель".
                </p>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold mb-3">Основные разделы</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium mb-2">Управление пользователями:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Создание и редактирование пользователей</li>
                      <li>• Назначение ролей и прав доступа</li>
                      <li>• Блокировка и разблокировка аккаунтов</li>
                      <li>• Просмотр активности пользователей</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium mb-2">Настройки системы:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Конфигурация базы данных</li>
                      <li>• Управление органами управления</li>
                      <li>• Настройки интеграций</li>
                      <li>• Импорт и экспорт данных</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold mb-3">API управление</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Раздел для управления API ключами и интеграциями:
                </p>
                <ul className="space-y-2 text-sm">
                  <li>• Создание и управление API ключами</li>
                  <li>• Настройка лимитов и разрешений</li>
                  <li>• Мониторинг использования API</li>
                  <li>• Документация по API</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shortcuts Tab */}
        <TabsContent value="shortcuts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Keyboard className="h-5 w-5" />
                Горячие клавиши
              </CardTitle>
              <CardDescription>
                Быстрые сочетания клавиш для повышения эффективности работы
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Навигация</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Поиск</span>
                      <Badge variant="outline">Ctrl + F</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Создать объект</span>
                      <Badge variant="outline">Ctrl + N</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Сохранить</span>
                      <Badge variant="outline">Ctrl + S</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Отменить</span>
                      <Badge variant="outline">Ctrl + Z</Badge>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Карта</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Увеличить масштаб</span>
                      <Badge variant="outline">+</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Уменьшить масштаб</span>
                      <Badge variant="outline">-</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Центрировать карту</span>
                      <Badge variant="outline">Home</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Полноэкранный режим</span>
                      <Badge variant="outline">F11</Badge>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Редактирование</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Выделить все</span>
                      <Badge variant="outline">Ctrl + A</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Копировать</span>
                      <Badge variant="outline">Ctrl + C</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Вставить</span>
                      <Badge variant="outline">Ctrl + V</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Удалить</span>
                      <Badge variant="outline">Delete</Badge>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Инструменты</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Инструмент точки</span>
                      <Badge variant="outline">P</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Инструмент линии</span>
                      <Badge variant="outline">L</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Инструмент полигона</span>
                      <Badge variant="outline">O</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Инструмент выбора</span>
                      <Badge variant="outline">S</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground">
        <p>
          Если у вас остались вопросы, обратитесь к администратору системы или
          воспользуйтесь технической поддержкой.
        </p>
      </div>
    </div>
  );
}